import { classes, events } from '@theme/constants';
import { publish } from '@theme/event-bus';
import { fetchConfig, json } from '@theme/fetch';
import { getCartSectionNames, updateQueryParam } from '@theme/helpers';
import { HTMLCustomElement } from '@theme/html-custom-element';

class ProductForm extends HTMLCustomElement {
  connectedCallback() {
    if (super.connectedCallback) super.connectedCallback();

    this.on('submit', this.refs.form, this.handleFormSubmit.bind(this));
    this.on('change', this.refs.option && this.refs.option.all, this.handleOptionChange.bind(this));
    this.on('change', this.refs.sellingPlanOption, this.handleOptionChange.bind(this));
    this.on('change', this.refs.giftCardFormCheckbox, this.handleGiftCardFormCheckbox.bind(this));
    this.on('change', this.refs.purchaseType && this.refs.purchaseType.all, this.handlePurchaseTypeChange.bind(this));

    /**
     * Sets the initial purchase type.
     */
    const purchaseType = this.getLoadPurchaseType();

    if (purchaseType && this.refs.purchaseType) {
      for (const element of this.refs.purchaseType.all) {
        element.checked = element.value === purchaseType;

        /**
         * Handle the change without updating query params.
         */
        this.handleOptionChange(false);
      }
    }
  }

  /**
   * Handles the change of the purchase type options.
   */
  handlePurchaseTypeChange() {
    const purchaseType = this.getDefaultPurchaseType() === this.getPurchaseType() ? null : this.getPurchaseType();

    this.handleOptionChange();

    /**
     * Doesn't set the `purchase_type` query param for subscriptions, since they use `selling_plan`.
     */
    if (!(purchaseType === 'subscription')) {
      updateQueryParam('purchase_type', purchaseType);
    }

    if (this.getPurchaseType() === 'one-time') {
      updateQueryParam('selling_plan', null);
    }
  }

  /**
   * Handles the option change event.
   */
  handleOptionChange(updateQueryParams = true) {
    const values =
      this.refs.option?.all
        .map((option) => (option.type === 'select-one' ? option.selectedOptions[0] : option.checked ? option : null))
        .filter(Boolean)
        .map(({ dataset }) => dataset.optionValueId) ?? [];

    if (updateQueryParams && this.refs.sellingPlanOption) {
      updateQueryParam('selling_plan', this.getSellingPlanForQueryParam());
    }

    this.dispatchEvent(
      new CustomEvent('option-change', {
        detail: {
          values,
          selling_plan: this.getPurchaseType() === 'subscription' ? this.refs.sellingPlanOption.value : null,
        },
      }),
    );
  }

  /**
   * Returns the selected purchase type.
   * @returns {'subscription'|'one-time'}
   */
  getPurchaseType() {
    return this.refs.purchaseType?.all.find(({ checked }) => checked)?.value;
  }

  /**
   * Returns the default purchase type.
   * @returns {'subscription'|'one-time'}
   */
  getDefaultPurchaseType() {
    return this.getAttribute('default-purchase-type') ?? 'one-time';
  }

  /**
   * Returns the purchase type used on page load.
   * @returns {'subscription'|'one-time'}
   */
  getLoadPurchaseType() {
    const params = new URLSearchParams(location.search);

    if (params.has('selling_plan')) {
      return 'subscription';
    }

    return params.get('purchase_type') ?? this.getDefaultPurchaseType();
  }

  /**
   * Returns the selling plan to push to the search parameters.
   * @returns {string|null}
   */
  getSellingPlanForQueryParam() {
    const { value, dataset } = this.refs.sellingPlanOption;

    if (
      this.getPurchaseType() === 'one-time' ||
      (this.getDefaultPurchaseType() === 'subscription' && value === dataset.default)
    ) {
      return null;
    }

    return value;
  }

  /**
   * Handles the form submit event.
   */
  handleFormSubmit(event) {
    const body = new FormData(event.target);

    event.preventDefault();

    this.setAddingState();
    this.setFormMessage();

    /**
     * Include the cart sections in the response.
     */
    body.append('sections', getCartSectionNames());

    try {
      json(fetch(theme.routes.cart_add_url, fetchConfig('javascript', { body }))).then((response) => {
        this.handleSubmitResponse(response);
        this.setAddingState(true);
      });
    } catch {
      event.target.submit();
    }
  }

  /**
   * Handles the form submit response.
   * @param {object} response - The form response.
   */
  handleSubmitResponse(response) {
    if (response.message) {
      this.setFormMessage(response.message);
      return;
    }

    publish(events.cartUpdated);
    publish(events.cartAdded);
    publish(events.sectionUpdate, { sections: response.sections });

    if (this.refs.quantity) {
      this.refs.quantity.value = 1;
    }
  }

  /**
   * Handles the gift card recipient form checkbox change.
   * @param {InputEvent} event - The checkbox event.
   */
  handleGiftCardFormCheckbox(event) {
    if (this.refs.giftCardField) {
      for (const element of this.refs.giftCardField.all) {
        element.classList.toggle(classes.hidden, !event.target.checked);
      }
    }

    if (this.refs.giftCardInput) {
      for (const element of this.refs.giftCardInput.all) {
        element.disabled = !event.target.checked;
      }
    }
  }

  /**
   * Sets the form message state.
   * @param {string} text - The message to set.
   */
  setFormMessage(text = '') {
    if (!this.refs.message) return;

    this.refs.message.innerHTML = text;
  }

  /**
   * Sets the form's "adding" state, when adding to cart.
   * @param {boolean} remove - Removes the adding state.
   */
  setAddingState(remove = false) {
    if (!this.refs.submit) return;

    if (!this.buttonContent) {
      this.buttonContent = this.refs.submit.innerHTML;
    }

    this.refs.submit.innerHTML = remove ? this.buttonContent : theme.strings.product.adding_to_cart;
    this.refs.submit.disabled = !remove;
  }
}

if (!customElements.get('product-form')) customElements.define('product-form', ProductForm);
